import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'

// Third party libraries
import { produce } from 'immer'

// ** Type Imports
import {
  ActivePeriodLengthType,
  ApplicationStageType,
  CheckboxUIConfigType,
  DiscountFormErrorState,
  DiscountFormStateType,
  DiscountSetupConfigType,
  DiscountSetupUIConfigType,
  DiscountTableDataType,
  DiscountUsageLimitsConfigType,
  DiscountUsageLimitsUIConfigType,
  FeatherIconType,
  GenerationType,
  InputUIConfigType
} from 'src/lib/types/discounts'

// ** Util Imports
import InputTextField from 'src/components/common/form-elements/InputTextField'
import { getUIConfig } from 'src/lib/utils/discountHelpers'

// ** Child Imports
import FormTile from 'src/components/common/containers/FormTile'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'
import { Typography } from '@mui/material'
import AdvanceDateTimeInput from 'src/components/common/form-elements/AdvanceDateTimeInput'
import { errorMappings } from 'src/lib/utils/validations/discountsValidations'
import { defaultErrorState, stackabilityRadioGroupOptions } from 'src/assets/DiscountsConfig'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import * as Icon from 'react-feather'
import { getValueInRange } from 'src/lib/utils/helpers'
import { getCombinableDiscounts, getDiscounts } from 'src/lib/core/api-methods'
import { discountsAdapter } from 'src/lib/utils/adapters'
import { Info } from 'react-feather'
import FloRadioGroup from 'src/components/common/form-elements/FloRadioGroup'
import SearchAsYouType from 'src/components/common/catalogue-search-component/subcomponents/SearchAsYouType'
import CustomizableDiscountsTable from 'src/components/ui-customization/sub-components/discounts-customization-list/CustomizableDiscountsTable'
import { useLocale } from 'src/lib/hooks/useLocal'
import Permission from 'src/services/PermissionService'
import { DiscountCallArgCouponType, permissionType } from 'src/lib/utils/constants'
import { PaginationState } from '@tanstack/react-table'

interface DiscountUsageLimitsFormProps {
  uiConfig: DiscountUsageLimitsUIConfigType
  setFormState: Dispatch<SetStateAction<DiscountFormStateType | undefined>>
  formState: DiscountUsageLimitsConfigType | undefined
  isEditMode: boolean
  generationType: GenerationType | undefined
  periodLength: ActivePeriodLengthType | undefined
  applicationStage: ApplicationStageType | undefined
  formErrors: DiscountFormErrorState
  setFormErrors: Dispatch<SetStateAction<DiscountFormErrorState>>
  setupStateConfig: DiscountSetupConfigType | undefined
  setupUIConfig: DiscountSetupUIConfigType | undefined
  discountId?: string
}

const DiscountUsageLimitsForm: React.FC<DiscountUsageLimitsFormProps> = ({
  uiConfig,
  formState,
  isEditMode,
  setFormState,
  generationType,
  periodLength,
  applicationStage,
  formErrors,
  setFormErrors,
  setupUIConfig,
  setupStateConfig,
  discountId
}) => {
  const { t } = useLocale()
  const {
    getters: { merchant }
  } = useMerchant()
  const issuerId = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

  const { discountUsesConfig, stackabilityType, activePeriodConfig } = formState || {}

  const {
    discountUsesUIConfig: discountUsesUIConfigArray,
    stackabilityUIConfig: stackabilityUIConfigArray,
    activePeriodUIConfig
  } = uiConfig || {}

  const generationMethod =
    setupStateConfig?.applicationConfig?.discountGenerationConfig?.bulkGenerationConfig?.valueType

  const discountUsesUIConfig = getUIConfig(discountUsesUIConfigArray, { valueType: applicationStage })
  const stackabilityUIConfig = getUIConfig(stackabilityUIConfigArray, { valueType: applicationStage })

  const totalUsesInputUIConfg: InputUIConfigType = getUIConfig(
    discountUsesUIConfig?.totalUsesInput,
    discountUsesConfig?.maxUsesCheckboxConfig
  )
  const totalUsesCheckboxConfig: CheckboxUIConfigType = getUIConfig(discountUsesUIConfig?.totalUsesCheckbox, {
    valueType: generationType
  })

  const usesPerCustomerCheckbox: CheckboxUIConfigType = getUIConfig(discountUsesUIConfig?.usesPerCustomerCheckbox, {
    valueType: generationType
  })
  const usesPerCustomerInputUIConfig: InputUIConfigType = getUIConfig(
    discountUsesUIConfig?.usesPerCustomerInput,
    discountUsesConfig?.maxUsesPerCustomerCheckboxConfig
  )

  // const oncePerCustomerCheckboxConfig: CheckboxUIConfigType = getUIConfig(
  //   discountUsesUIConfig?.oncePerCustomerCheckbox,
  //   { valueType: generationType }
  // )
  const combineCheckboxConfig: CheckboxUIConfigType = getUIConfig(
    stackabilityUIConfig?.combineCheckbox,
    setupStateConfig?.applicationMethodConfig
  )
  const startDataInputConfig: InputUIConfigType = getUIConfig(activePeriodUIConfig?.startDateInput, {
    valueType: periodLength
  })
  const startTimeInputConfig: InputUIConfigType = getUIConfig(activePeriodUIConfig?.startTimeInput, {
    valueType: periodLength
  })
  const endDateCheckboxUIConfig: CheckboxUIConfigType = getUIConfig(activePeriodUIConfig?.endDateCheckbox, {
    valueType: periodLength
  })
  const endTimeInputConfig: InputUIConfigType = getUIConfig(
    activePeriodUIConfig?.endTimeInput,
    activePeriodConfig?.endDataCheckboxConfig
  )
  const endDateInputConfig: InputUIConfigType = getUIConfig(
    activePeriodUIConfig?.endDateInput,
    activePeriodConfig?.endDataCheckboxConfig
  )
  const activePeriodInputConfig: InputUIConfigType = getUIConfig(activePeriodUIConfig?.activePeriodInput, {
    valueType: periodLength
  })

  // const [page, setPage] = useState<number>(0)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10
  })
  // const newrowsPerPage = pagination.pageSize
  const page = pagination.pageIndex
  const [totalRowCount, setTotalRowCount] = useState<number>(0)
  const [selectedDiscounts, setSelectedDiscounts] = useState<Array<any>>([])

  const [searchedText, setSearchedText] = useState<string>('')
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false)
  const [searchedDiscountsTableData, setSearchedDiscountsTableData] = useState<DiscountTableDataType[]>([])

  useEffect(() => {
    if (!Boolean(merchant)) return
    callGetDiscounts()
    handleChangePage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedText, page, merchant])

  const callGetDiscounts = async () => {
    try {
      setIsSearchLoading(true)
      const searchedDiscountResponse: any = await getDiscounts(
        issuerId,
        merchant?.accountId,
        10,
        page + 1,
        'discounts',
        '',
        searchedText,
        undefined,
        DiscountCallArgCouponType,
        'CUSTOM'
      )
      const rows = searchedDiscountResponse?.data?.data
        ?.filter((row: any, index: number) => Boolean(discountId !== row?.coupon_id))
        ?.map((row: any, index: number) => {
          return discountsAdapter(row, index + 1)
        })
      setSearchedDiscountsTableData(rows)
      setTotalRowCount(searchedDiscountResponse?.data?.total_count)
      setIsSearchLoading(false)
    } catch (error) {
      setIsSearchLoading(false)
      console.error(error)
    }
  }

  const handleChangePage = (newPage: number) => {
    setPagination(prev => ({
      ...prev,
      pageIndex: page
    }))
  }

  useEffect(() => {
    // if (typeof oncePerCustomerCheckboxConfig?.defaultValue !== 'undefined') {
    //   setFormState(
    //     produce((draft: any) => {
    //       draft.usageLimits.discountUsesConfig.oncePerCustomer = oncePerCustomerCheckboxConfig?.defaultValue
    //       draft.usageLimits.isStackable = combineCheckboxConfig?.defaultValue
    //     })
    //   )
    // }
    // if (generationType === "BULK") {
    //   setFormState(produce((draft: any) => {
    //     draft.usageLimits.discountUsesConfig.maxUses = 1;
    //   }))
    // }

    if (typeof totalUsesCheckboxConfig?.defaultValue !== 'undefined') {
      setFormState(
        produce((draft: any) => {
          draft.usageLimits.discountUsesConfig.maxUsesCheckboxConfig.valueType = totalUsesCheckboxConfig?.defaultValue
        })
      )
    }
    if (typeof usesPerCustomerCheckbox?.defaultValue !== 'undefined') {
      setFormState(
        produce((draft: any) => {
          draft.usageLimits.discountUsesConfig.maxUsesPerCustomerCheckboxConfig.valueType =
            usesPerCustomerCheckbox?.defaultValue
        })
      )
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiConfig])

  const renderUsesSection = () => {
    return (
      <FormTile title={discountUsesUIConfig?.title}>
        <div className='flex flex-col w-full gap-3'>
          {generationType === 'BULK' ? (
            <div className='flex gap-1 items-start bg-[#91C1E31A] border border-[#91C1E3] rounded-lg p-2'>
              <div className='w-4 h-5 flex items-center'>
                <Info className='text-[#407BA5] h-4 w-4 stroke-[2.5px]' />
              </div>
              {generationMethod === 'RANDOM_CODES' ? (
                <p className='text-[#407BA5] text-sm'>
                  Each code is limited to one user per customer when created as part of a set.
                </p>
              ) : (
                <p className='text-[#407BA5] text-sm flex flex-col gap-3'>
                  Set usage limits for each code in your CSV file. The default limit is one use per code, unless
                  specified otherwise in your file.
                  <span>
                    <span className='font-semibold'>Note:</span> Codes in sets can only be used once per customer.
                  </span>
                </p>
              )}
            </div>
          ) : (
            <></>
          )}
          {!(generationType === 'BULK' && generationMethod === 'CSV_UPLOAD') ? (
            <>
              <div className={`${totalUsesCheckboxConfig?.isHidden ? 'hidden' : ''}`}>
                <FloCheckbox
                  isDisabled={totalUsesCheckboxConfig?.isDisabled}
                  value={Boolean(discountUsesConfig?.maxUsesCheckboxConfig?.valueType)}
                  label={totalUsesCheckboxConfig?.label}
                  setValue={(checked: boolean) => {
                    setFormState(
                      produce((draft: any) => {
                        draft.usageLimits.discountUsesConfig.maxUsesCheckboxConfig.valueType = checked
                      })
                    )
                  }}
                />
              </div>
              <div className={`${totalUsesInputUIConfg?.isHidden ? 'hidden' : ''} w-1/2 pl-6`}>
                <InputTextField
                  textFieldId='total-uses-input'
                  label={totalUsesInputUIConfg?.label}
                  inputValue={discountUsesConfig?.maxUses}
                  inputProps={{
                    min: 1,
                    max: 999999,
                    onKeyDown: (e: any) => {
                      if (e.key === '+' || e.key === '-') {
                        e.preventDefault()
                      }
                    }
                  }}
                  handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFormState(
                      produce((draft: any) => {
                        draft.usageLimits.discountUsesConfig.maxUses = getValueInRange(
                          event.target.value,
                          999999,
                          1,
                          true
                        )
                      })
                    )
                  }}
                  inputType={'number'}
                  inputModeType={'tel'}
                  errorStatus={formErrors?.maxUses?.status}
                  errorMessage={errorMappings?.[formErrors?.maxUses?.errorCode as keyof typeof errorMappings]}
                  handleFocus={() => setFormErrors({ ...formErrors, maxUses: defaultErrorState })}
                />
              </div>

              <FloCheckbox
                value={Boolean(discountUsesConfig?.maxUsesPerCustomerCheckboxConfig?.valueType)}
                label={usesPerCustomerCheckbox?.label}
                isDisabled={usesPerCustomerCheckbox?.isDisabled}
                setValue={(checked: boolean) => {
                  setFormState(
                    produce((draft: any) => {
                      draft.usageLimits.discountUsesConfig.maxUsesPerCustomerCheckboxConfig.valueType = checked
                    })
                  )
                }}
              />
              <div className={`${usesPerCustomerInputUIConfig?.isHidden ? 'hidden' : ''} w-1/2 pl-6`}>
                <InputTextField
                  textFieldId='total-uses-input'
                  isDisabled={generationType === 'BULK'}
                  label={usesPerCustomerInputUIConfig?.label}
                  inputValue={generationType === 'BULK' ? '1' : discountUsesConfig?.maxUsesPerCustomer}
                  inputProps={{
                    min: 1,
                    max: 999999,
                    onKeyDown: (e: any) => {
                      if (e.key === '+' || e.key === '-') {
                        e.preventDefault()
                      }
                    }
                  }}
                  handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFormState(
                      produce((draft: any) => {
                        draft.usageLimits.discountUsesConfig.maxUsesPerCustomer = getValueInRange(
                          event.target.value,
                          999999,
                          1,
                          true
                        )
                      })
                    )
                  }}
                  inputType={'number'}
                  inputModeType={'tel'}
                  errorStatus={formErrors?.maxUsesPerCustomer?.status}
                  errorMessage={
                    errorMappings?.[formErrors?.maxUsesPerCustomer?.errorCode as keyof typeof errorMappings]
                  }
                  handleFocus={() => setFormErrors({ ...formErrors, maxUsesPerCustomer: defaultErrorState })}
                />
              </div>
            </>
          ) : (
            <></>
          )}
        </div>
      </FormTile>
    )
  }

  const renderStackabilitySection = () => {
    return (
      <FormTile title={stackabilityUIConfig?.title} subtitle={t('allow_combine_discounts_with_other_discounts')}>
        <div className='flex flex-col w-full gap-3'>
          <div className={`${stackabilityUIConfig?.stackabilityRadioGroup?.isHidden ? 'hidden' : ''}`}>
            <Permission
              permissionId={permissionType?.GROUP_DISCOUNTS}
              UnavailabilityFallbackUI={
                <FloRadioGroup
                  selectedOption={stackabilityType?.valueType}
                  options={stackabilityRadioGroupOptions}
                  groupName={'entitled-catalogue'}
                  setSelectedOption={(selectedOption: any) => {
                    setFormState(
                      produce((draft: any) => {
                        draft.usageLimits.stackabilityType.valueType = selectedOption
                        if (selectedOption === 'CUSTOM' && !Boolean(stackabilityType?.discountIDs)) {
                          draft.usageLimits.stackabilityType.discountIDs = []
                        }
                      })
                    )
                  }}
                />
              }
            >
              <FloRadioGroup
                selectedOption={stackabilityType?.valueType}
                options={stackabilityUIConfig?.stackabilityRadioGroup?.radioItemsUIConfig}
                groupName={'entitled-catalogue'}
                setSelectedOption={(selectedOption: any) => {
                  setFormState(
                    produce((draft: any) => {
                      draft.usageLimits.stackabilityType.valueType = selectedOption
                      if (selectedOption === 'CUSTOM' && !Boolean(stackabilityType?.discountIDs)) {
                        draft.usageLimits.stackabilityType.discountIDs = []
                      }
                    })
                  )
                }}
              />
            </Permission>
          </div>
        </div>
        {stackabilityType?.valueType === 'CUSTOM' && stackabilityType?.discountIDs && (
          <FormTile
            title={t('select_from_active_discount_codes')}
            subtitle={t('list_includes_active_custom_discounts')}
          >
            <SearchAsYouType
              autoFocus
              hideBrowseButton
              setSearchedText={setSearchedText}
              placeholder={t('select_discounts')}
            />
            <CustomizableDiscountsTable
              page={page}
              isLoading={isSearchLoading}
              totalRowCount={totalRowCount}
              callGetAllDiscounts={callGetDiscounts}
              rows={searchedDiscountsTableData}
              handleChangePage={handleChangePage}
              setSelectedDiscountIds={selectedDiscountIds => {
                setFormState(
                  produce((draft: any) => {
                    draft.usageLimits.stackabilityType.discountIDs = selectedDiscountIds
                  })
                )
              }}
              selectedDiscountIds={stackabilityType?.discountIDs}
              selectedDiscounts={selectedDiscounts}
              setSelectedDiscounts={setSelectedDiscounts}
              isOnboarding={true}
              hideActive
              pagination={{
                paginationState: pagination,
                setPaginationState: setPagination
              }}
            />
          </FormTile>
        )}
      </FormTile>
    )
  }

  const getIconComponent = (requiredIcon: any) => {
    switch (requiredIcon) {
      case '₹':
        return <>₹</>
      case '%':
        return <>%</>
      default:
        const IconComponent = Icon[requiredIcon as FeatherIconType]
        return <IconComponent className='scale-75' />
    }
  }

  const renderActivePeriodSection = () => {
    return (
      <FormTile title={activePeriodUIConfig?.title}>
        <div className='flex flex-col w-full gap-3'>
          <div className={` ${periodLength === 'LONG' ? 'hidden' : ''} w-1/3`}>
            <InputTextField
              errorMessage={errorMappings?.[formErrors?.activePeriod?.errorCode as keyof typeof errorMappings]}
              errorStatus={formErrors?.activePeriod?.status}
              handleFocus={() => setFormErrors({ ...formErrors, activePeriod: defaultErrorState })}
              textFieldId='active-period'
              label={activePeriodInputConfig?.label}
              {...(activePeriodInputConfig?.endIconComponent
                ? { endIcon: activePeriodInputConfig?.endIconComponent }
                : {})}
              inputValue={activePeriodConfig?.activePeriod}
              inputProps={{ min: 0, max: 30 }}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFormState(
                  produce((draft: any) => {
                    draft.usageLimits.activePeriodConfig.activePeriod = event.target.value
                  })
                )
              }}
              inputType={'number'}
              inputModeType={'tel'}
            />
          </div>
          <div
            className={`flex items-center w-2/3 gap-2 
                        ${periodLength === 'SHORT' ? 'hidden' : ''}`}
          >
            <AdvanceDateTimeInput
              errorMessage={errorMappings?.[formErrors?.startDate?.errorCode as keyof typeof errorMappings]}
              errorStatus={formErrors?.startDate?.status}
              handleFocus={() => setFormErrors({ ...formErrors, startDate: defaultErrorState })}
              iconComponent={
                startDataInputConfig?.startIconComponent ? (
                  getIconComponent(startDataInputConfig?.startIconComponent)
                ) : (
                  <></>
                )
              }
              type={startDataInputConfig?.type as 'date'}
              value={activePeriodConfig?.startDate}
              label={startDataInputConfig?.label}
              setValue={value => {
                setFormState(
                  produce((draft: any) => {
                    draft.usageLimits.activePeriodConfig.startDate = value
                  })
                )
              }}
            />
            <AdvanceDateTimeInput
              errorMessage={errorMappings?.[formErrors?.startDate?.errorCode as keyof typeof errorMappings]}
              errorStatus={formErrors?.startDate?.status}
              handleFocus={() => setFormErrors({ ...formErrors, startDate: defaultErrorState })}
              iconComponent={
                startTimeInputConfig?.startIconComponent ? (
                  getIconComponent(startTimeInputConfig?.startIconComponent)
                ) : (
                  <></>
                )
              }
              type={startTimeInputConfig?.type as 'date'}
              value={activePeriodConfig?.startDate}
              label={startTimeInputConfig?.label}
              setValue={value => {
                setFormState(
                  produce((draft: any) => {
                    draft.usageLimits.activePeriodConfig.startDate = value
                  })
                )
              }}
            />
          </div>
          <div className={` ${periodLength === 'SHORT' ? 'hidden' : ''}`}>
            <FloCheckbox
              value={Boolean(activePeriodConfig?.endDataCheckboxConfig?.valueType)}
              label={endDateCheckboxUIConfig?.label}
              setValue={(checked: boolean) => {
                setFormState(
                  produce((draft: any) => {
                    draft.usageLimits.activePeriodConfig.endDataCheckboxConfig.valueType = checked
                  })
                )
              }}
            />
          </div>
          <div
            className={`flex items-center w-2/3 gap-2 
                        ${periodLength === 'SHORT' || endDateInputConfig?.isHidden ? 'hidden' : ''}`}
          >
            <AdvanceDateTimeInput
              errorMessage={errorMappings?.[formErrors?.endDate?.errorCode as keyof typeof errorMappings]}
              errorStatus={formErrors?.endDate?.status}
              handleFocus={() => setFormErrors({ ...formErrors, endDate: defaultErrorState })}
              iconComponent={
                endDateInputConfig?.startIconComponent ? (
                  getIconComponent(endDateInputConfig?.startIconComponent)
                ) : (
                  <></>
                )
              }
              type={endDateInputConfig?.type as 'date'}
              value={activePeriodConfig?.endDate}
              label={endDateInputConfig?.label}
              setValue={value => {
                setFormState(
                  produce((draft: any) => {
                    draft.usageLimits.activePeriodConfig.endDate = value
                  })
                )
              }}
            />
            <AdvanceDateTimeInput
              errorMessage={errorMappings?.[formErrors?.endDate?.errorCode as keyof typeof errorMappings]}
              errorStatus={formErrors?.endDate?.status}
              handleFocus={() => setFormErrors({ ...formErrors, endDate: defaultErrorState })}
              iconComponent={
                endTimeInputConfig?.startIconComponent ? (
                  getIconComponent(endTimeInputConfig?.startIconComponent)
                ) : (
                  <></>
                )
              }
              type={endTimeInputConfig?.type as 'date'}
              value={activePeriodConfig?.endDate}
              label={endTimeInputConfig?.label}
              setValue={value => {
                setFormState(
                  produce((draft: any) => {
                    draft.usageLimits.activePeriodConfig.endDate = value
                  })
                )
              }}
            />
          </div>
        </div>
      </FormTile>
    )
  }

  return (
    <>
      <div id='uses' className={`${discountUsesUIConfig?.isHidden ? 'hidden' : ''}`}>
        {renderUsesSection()}
      </div>

      <div id='stackability' className={`${stackabilityUIConfig?.isHidden ? 'hidden' : ''}`}>
        {renderStackabilitySection()}
      </div>

      <div id='active-period' className={`${activePeriodUIConfig?.isHidden ? 'hidden' : ''}`}>
        {renderActivePeriodSection()}
      </div>
    </>
  )
}

export default DiscountUsageLimitsForm
